import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
    OnboardingDebugAccountStatus,
    type OnboardingDebugFile,
    type OnboardingDebugMergedHousehold,
    type OnboardingDebugView,
} from '@ndvr/customer-api';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DateTimeDisplay } from 'widgets';

import { useOnboardingDebugViewQuery } from './onboarding-debug-queries';

const HOUSEHOLD_PARAM = 'household';

/** Support's view of one household's onboarding: its status, build state and every file with what was read from it. */
export const OnboardingDebugAdmin = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const householdGUID = searchParams.get(HOUSEHOLD_PARAM) ?? undefined;
    const [input, setInput] = useState(householdGUID ?? '');
    const { data, isFetching, error } = useOnboardingDebugViewQuery(householdGUID);

    const show = (event: React.FormEvent) => {
        event.preventDefault();
        setSearchParams(input.trim() ? { [HOUSEHOLD_PARAM]: input.trim() } : {});
    };

    return (
        <div className="flex flex-col gap-4">
            <form className="flex max-w-xl gap-2" onSubmit={show}>
                <Input placeholder="Household GUID" value={input} onChange={(event) => setInput(event.target.value)} />
                <Button type="submit" disabled={!input.trim() || isFetching}>
                    Show
                </Button>
            </form>
            {error && <p className="text-destructive text-sm">Could not load the onboarding of this household.</p>}
            {data && <OnboardingDebugDetails debug={data} />}
        </div>
    );
};

const OnboardingDebugDetails = ({ debug }: { debug: OnboardingDebugView }) => (
    <>
        <Card>
            <CardHeader>
                <CardTitle>Status</CardTitle>
            </CardHeader>
            <CardContent>
                <Fields
                    fields={[
                        ['Stage', debug.status.stage],
                        ['Document reading', debug.status.documentReadState],
                        ['Move confirmed', <DateTimeDisplay key="move" dateString={debug.status.moveConfirmedAt} />],
                        [
                            'Upload confirmed',
                            <DateTimeDisplay key="upload" dateString={debug.status.fileUploadConfirmedAt} />,
                        ],
                        ['Build started', <DateTimeDisplay key="build" dateString={debug.householdBuildStartedAt} />],
                        ['Built account numbers', debug.builtAccountNumbers.join(', ') || '—'],
                        [
                            'Built people',
                            debug.builtPeople
                                .map((person) => `${person.personOrgGUID} (${person.taxIdLast4 ?? 'no tax ID'})`)
                                .join(', ') || '—',
                        ],
                    ]}
                />
            </CardContent>
        </Card>
        <MergedHouseholdCard merged={debug.mergedHousehold} error={debug.mergeError} />
        {debug.files.map((file) => (
            <OnboardingDebugFileCard key={file.onboardingFileGUID} file={file} />
        ))}
    </>
);

const STATUS_BADGE = {
    [OnboardingDebugAccountStatus.BUILT]: 'success',
    [OnboardingDebugAccountStatus.PENDING]: 'secondary',
    [OnboardingDebugAccountStatus.SKIPPED]: 'destructive',
} as const;

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** What building now would build from the files read so far, and which accounts were built already. */
const MergedHouseholdCard = ({
    merged,
    error,
}: {
    merged?: OnboardingDebugMergedHousehold | null;
    error?: string | null;
}) => (
    <Card>
        <CardHeader>
            <CardTitle>Merged household</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 text-sm">
            {!merged && <p className="text-destructive">The files could not be merged: {error ?? 'unknown error'}</p>}
            {merged && <MergedHouseholdDetails merged={merged} />}
        </CardContent>
    </Card>
);

const MergedHouseholdDetails = ({ merged }: { merged: OnboardingDebugMergedHousehold }) => (
    <>
        <table className="w-full text-left">
            <thead className="text-muted-foreground">
                <tr>
                    <th className="py-1 pr-4 font-normal">Account</th>
                    <th className="py-1 pr-4 font-normal">Owners</th>
                    <th className="py-1 pr-4 text-right font-normal">Value</th>
                    <th className="py-1 font-normal">Status</th>
                </tr>
            </thead>
            <tbody>
                {merged.accounts.map((account) => (
                    <tr key={account.accountNumber} className="border-t align-top">
                        <td className="py-1 pr-4">
                            {account.accountName}
                            {account.inherited && ' (inherited)'}
                        </td>
                        <td className="py-1 pr-4">{account.trust ?? (account.owners.join(' & ') || '—')}</td>
                        <td className="py-1 pr-4 text-right">
                            {account.endingValue == null ? '—' : currency.format(account.endingValue)}
                        </td>
                        <td className="py-1">
                            <Badge variant={STATUS_BADGE[account.status]}>{account.status}</Badge>
                            {account.skipReason && (
                                <span className="text-muted-foreground ml-2">{account.skipReason}</span>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
        <Fields
            fields={[
                ...merged.people.map((person): [string, React.ReactNode] => [
                    person.name,
                    [
                        `key ${person.key}`,
                        person.dateOfBirth && `born ${person.dateOfBirth}`,
                        person.address,
                        person.taxIdLast4 && `SSN …${person.taxIdLast4}`,
                    ]
                        .filter(Boolean)
                        .join(' · ') || '—',
                ]),
                ...merged.trusts.map((trust): [string, React.ReactNode] => [
                    trust.name,
                    [
                        trust.dateEstablished && `established ${trust.dateEstablished}`,
                        `trustees: ${trust.trustees.join(', ') || '—'}`,
                    ]
                        .filter(Boolean)
                        .join(' · '),
                ]),
            ]}
        />
    </>
);

const OnboardingDebugFileCard = ({ file }: { file: OnboardingDebugFile }) => (
    <Card>
        <CardHeader>
            <CardTitle className="flex items-center gap-2">
                {file.fileName}
                <Badge variant="secondary">{file.processingStatus}</Badge>
                {file.processingIssue && <Badge variant="destructive">{file.processingIssue}</Badge>}
                {file.deletedAt && <Badge variant="outline">Removed</Badge>}
            </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
            <Fields
                fields={[
                    ['File', file.onboardingFileGUID],
                    ['Type', `${file.contentType}, ${Math.round(file.sizeBytes / 1024)} KB`],
                    ['Uploaded', <DateTimeDisplay key="uploaded" dateString={file.uploadedAt} />],
                    ['Removed', <DateTimeDisplay key="removed" dateString={file.deletedAt} />],
                    ['Document type', file.documentType ?? '—'],
                    ['Reading started', <DateTimeDisplay key="started" dateString={file.processingStartedAt} />],
                    ['Reading attempts', file.processingAttempts],
                ]}
            />
            {file.extraction ? (
                <details>
                    <summary className="cursor-pointer text-sm">
                        Extraction by {file.extraction.model}, prompt {file.extraction.promptVersion},{' '}
                        {file.extraction.inputTokens} input / {file.extraction.outputTokens} output tokens
                    </summary>
                    <pre className="bg-muted mt-2 max-h-[32rem] overflow-auto rounded-md p-3 text-xs">
                        {file.extraction.result}
                    </pre>
                </details>
            ) : (
                <p className="text-muted-foreground text-sm">Not read yet</p>
            )}
        </CardContent>
    </Card>
);

const Fields = ({ fields }: { fields: [string, React.ReactNode][] }) => (
    <dl className="grid grid-cols-[12rem_1fr] gap-x-4 gap-y-1 text-sm">
        {fields.map(([label, value]) => (
            <React.Fragment key={label}>
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="break-all">{value}</dd>
            </React.Fragment>
        ))}
    </dl>
);
